/**
 * Back-in-stock dialog — opens from Notify CTAs and posts to Klaviyo's
 * Client Back in Stock API for the currently selected variant. When the
 * shopper ticks marketing consent, the email is also subscribed to the
 * marketing list from theme settings.
 */
(() => {
  const TAG = 'back-in-stock-dialog';

  if (customElements.get(TAG)) return;

  const KLAVIYO_REVISION = '2024-10-15';

  const catalogVariantId = (variantId) =>
    `$shopify:::$default:::${variantId}`;

  const klaviyoPost = (path, companyId, data) =>
    fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({ data }),
    });

  // Records the consent source in Klaviyo alongside the subscription
  const CONSENT_SOURCE = 'Back in stock';

  const findDialogHost = (trigger) => {
    const productForm = trigger.closest('product-form');

    if (productForm) {
      return productForm.querySelector(TAG);
    }

    const sticky = trigger.closest('sticky-atc');
    const formId = sticky?.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;

    return form?.closest('product-form')?.querySelector(TAG) || null;
  };

  class BackInStockDialog extends HTMLElement {
    #dialog = null;

    connectedCallback() {
      this.#dialog = this.querySelector('dialog');
      this.querySelector('[data-back-in-stock-close]')?.addEventListener(
        'click',
        this.#onClose
      );
      this.querySelector('[data-back-in-stock-form]')?.addEventListener(
        'submit',
        this.#onSubmit
      );
      this.#dialog?.addEventListener('close', this.#onDialogClose);
    }

    disconnectedCallback() {
      this.querySelector('[data-back-in-stock-close]')?.removeEventListener(
        'click',
        this.#onClose
      );
      this.querySelector('[data-back-in-stock-form]')?.removeEventListener(
        'submit',
        this.#onSubmit
      );
      this.#dialog?.removeEventListener('close', this.#onDialogClose);
    }

    open() {
      if (!(this.#dialog instanceof HTMLDialogElement)) return;
      if (this.#dialog.open) return;

      this.#setStatus('', '');
      this.querySelector('[data-back-in-stock-form]')?.removeAttribute('hidden');
      this.#dialog.showModal();
      this.querySelector('input[type="email"]')?.focus();
    }

    close() {
      if (!(this.#dialog instanceof HTMLDialogElement)) return;
      if (!this.#dialog.open) return;

      this.#dialog.close();
    }

    #onClose = () => {
      this.close();
    };

    #onDialogClose = () => {
      const form = this.querySelector('[data-back-in-stock-form]');

      form?.removeAttribute('hidden');
      this.#setStatus('', '');
    };

    #setStatus(message, tone) {
      const status = this.querySelector('[data-back-in-stock-status]');

      if (!(status instanceof HTMLElement)) return;

      status.hidden = !message;
      status.textContent = message;
      status.classList.toggle('back-in-stock__status--error', tone === 'error');
      status.classList.toggle(
        'back-in-stock__status--success',
        tone === 'success'
      );
    }

    #variantId() {
      const form = this.closest('product-form');
      const picker = form?.querySelector('product-variant-picker');
      const variant = picker?.getCurrentVariant?.();

      if (variant?.id) return String(variant.id);

      const input = form?.querySelector('input[name="id"]');

      return input instanceof HTMLInputElement ? input.value : '';
    }

    #submitButton() {
      return this.querySelector('.back-in-stock__submit');
    }

    #setBusy(busy) {
      const button = this.#submitButton();

      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = busy;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    // The client subscription itself records email marketing consent for the
    // list. Klaviyo rejects a profile `subscriptions` block on this endpoint
    // at KLAVIYO_REVISION (400), which left ticked shoppers unsubscribed.
    #subscribeToMarketing(companyId, listId, email) {
      return klaviyoPost('subscriptions', companyId, {
        type: 'subscription',
        attributes: {
          custom_source: CONSENT_SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: listId } },
        },
      });
    }

    #onSubmit = (event) => {
      event.preventDefault();

      const form = event.currentTarget;

      if (!(form instanceof HTMLFormElement)) return;

      const formData = new FormData(form);
      const email = String(formData.get('email') || '').trim();
      const marketingConsent = formData.get('marketing_consent') === 'on';
      const companyId = this.dataset.companyId || '';
      const listId = this.dataset.marketingListId || '';
      const variantId = this.#variantId();

      if (!companyId) {
        this.#setStatus(this.dataset.missingConfig || '', 'error');
        return;
      }

      if (!email || !variantId) {
        this.#setStatus(this.dataset.errorMessage || '', 'error');
        return;
      }

      this.#setBusy(true);
      this.#setStatus('', '');

      klaviyoPost('back-in-stock-subscriptions', companyId, {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: catalogVariantId(variantId),
            },
          },
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('klaviyo');
          }

          // The restock alert is what was asked for, so a failed opt-in does
          // not turn the confirmation into an error.
          if (marketingConsent && listId) {
            return this.#subscribeToMarketing(companyId, listId, email).catch(() => {});
          }

          return null;
        })
        .then(() => {
          form.hidden = true;
          this.#setStatus(this.dataset.successMessage || '', 'success');
        })
        .catch(() => {
          this.#setStatus(this.dataset.errorMessage || '', 'error');
        })
        .finally(() => {
          this.#setBusy(false);
        });
    };
  }

  customElements.define(TAG, BackInStockDialog);

  if (!window.__themeBackInStockTriggers) {
    window.__themeBackInStockTriggers = true;

    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target instanceof Element
          ? event.target.closest('[data-back-in-stock-trigger]')
          : null;

        if (!(trigger instanceof HTMLElement)) return;

        event.preventDefault();

        const host = findDialogHost(trigger);

        if (host && typeof host.open === 'function') {
          host.open();
        }
      },
      true
    );
  }
})();
